import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { capitalize } from '@ember/string';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template/-private/handlebars';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { eq, and, gt, or } from 'ember-truth-helpers';
import AdminEventData from 'garaje/components/event-log/admin-event-data';
import MonogramOrPhoto from 'garaje/components/monogram-or-photo';
import TableDrawer from 'garaje/components/table-drawer';
import UiCard from 'garaje/components/ui/card';
import UiButton from 'garaje/components/ui-button';
import dateFormat from 'garaje/helpers/date-format';
import type EventLogModel from 'garaje/models/event-log';
import type MetricsService from 'garaje/services/metrics';
import type SkinnyLocationsService from 'garaje/services/skinny-locations';

interface EventDataComponentSignature {
  Args: {
    eventType: string;
    eventData: string;
    formatValue: (value: unknown) => string;
  };
}

interface EventLogDrawerSignature {
  Args: {
    event: EventLogModel;
    isOpen: boolean;
    onClose: () => void;
    timezone?: string;
  };
  Blocks: {
    default?: [EventLogModel];
  };
}

type ChangeValue = {
  from: string | number | boolean | null | Record<string, unknown>;
  to: string | number | boolean | null | Record<string, unknown>;
};

type ChangesRecord = Record<string, ChangeValue>;

// date detection patterns
const TIMESTAMP_PATTERN = /^\d{13}$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z?$/;

// key formatting patterns
const UNDERSCORE_PATTERN = /_/g;
const CAMEL_CASE_PATTERN = /([a-z])([A-Z])/g;

// field exclusions mapping by event type pattern
const FIELD_EXCLUSIONS: Record<string, string[]> = {
  // default exclusions for all event types
  '*': ['changes'],

  'employee.*': ['employee_id', 'cost_center', 'external_id', 'remote_id', 'scim_user_id', 'user_id', 'location_type'],
  'integration.*': ['plugin_id', 'company_id'],
};

// fields that should only be displayed if they have non-empty values
const CONDITIONAL_DISPLAY_FIELDS: Record<string, string[]> = {
  'employee.*': ['phone_number', 'department', 'division', 'title', 'organization', 'manually_added', 'do_not_sync'],
};

function isChangesRecord(changes: unknown): changes is ChangesRecord {
  return changes !== null && typeof changes === 'object' && !Array.isArray(changes);
}

function isFromToObject(obj: unknown): obj is ChangeValue {
  return obj !== null && typeof obj === 'object' && !Array.isArray(obj) && 'from' in obj && 'to' in obj;
}

export default class EventLogDrawer extends Component<EventLogDrawerSignature> {
  @service declare skinnyLocations: SkinnyLocationsService;
  @service declare metrics: MetricsService;

  @tracked collapsedFields = new Set<string>();

  constructor(owner: unknown, args: EventLogDrawerSignature['Args']) {
    super(owner, args);
    if (args.isOpen) {
      this.#trackDrawerOpen();
    }
  }

  get eventData(): Record<string, unknown> {
    try {
      return <Record<string, unknown>>JSON.parse(this.args.event.event);
    } catch {
      return {};
    }
  }

  get changes(): ChangesRecord | null {
    const eventData = this.eventData;
    const changes = eventData['changes'];

    if (!isChangesRecord(changes)) {
      return null;
    }

    return this.#flattenChanges(changes);
  }

  get hasChanges(): boolean {
    return this.changes !== null && Object.keys(this.changes).length > 0;
  }

  get eventDataEntries(): Array<{
    key: string;
    value: unknown;
    isNested?: boolean;
    level?: number;
    id?: string;
    parentId?: string;
  }> {
    return this.#flattenEventData(this.eventData, 0, '');
  }

  get lowercaseActorType(): string {
    const actorType = this.args.event.actorType?.toLowerCase().replace(/_/g, ' ') || '';
    if (actorType === 'system') return '';
    return actorType;
  }

  get lowercaseTargetType(): string {
    return this.args.event.targetType?.toLowerCase().replace(/_/g, ' ') || '';
  }

  get visitorTargetLink(): { route: string; model: string; label: string } | null {
    const { targetType, targetId } = this.args.event;
    if (!targetId) return null;

    const normalizedType = targetType?.toLowerCase();
    if (normalizedType === 'invite') {
      return { route: 'visitors.invites.show', model: targetId, label: 'Invite' };
    }
    if (normalizedType === 'entry') {
      return { route: 'visitors.entry', model: targetId, label: 'Entry' };
    }

    return null;
  }

  get eventCategoryComponent(): typeof Component<EventDataComponentSignature> | null {
    if (this.args.event.eventType.startsWith('admin.')) {
      return AdminEventData as typeof Component<EventDataComponentSignature>;
    }

    // teams can add their category components here
    // e.g., if (this.isVisitorEvent) return VisitorEventData;
    return null;
  }

  @action
  close(): void {
    this.args.onClose();
  }

  @action
  toggleFieldCollapse(fieldId: string): void {
    if (this.collapsedFields.has(fieldId)) {
      this.collapsedFields.delete(fieldId);
    } else {
      this.collapsedFields.add(fieldId);
    }
    this.collapsedFields = new Set(this.collapsedFields);
  }

  isFieldCollapsed = (fieldId: string | undefined): boolean => {
    if (!fieldId) return false;
    return this.collapsedFields.has(fieldId);
  };

  formatValue = (value: unknown): string => {
    if (value === null || value === undefined) {
      return '-';
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.formatValue(item)).join(', ');
    }

    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    const stringValue = String(value);

    // handle timestamps (13-digit numbers)
    if (TIMESTAMP_PATTERN.test(stringValue)) {
      const date = new Date(parseInt(stringValue, 10));
      if (!isNaN(date.getTime())) {
        return this.formatDate(date);
      }
    }

    // handle ISO date strings
    if (ISO_DATE_PATTERN.test(stringValue)) {
      const isoDate = new Date(stringValue);
      const eventTime = this.args.event.dateTime?.getTime();

      // if the ISO date matches the event timestamp (within 1 second), use the event timestamp for millisecond precision
      if (eventTime && Math.abs(isoDate.getTime() - eventTime) < 1000) {
        return this.formatDate(this.args.event.dateTime!);
      }

      if (!isNaN(isoDate.getTime())) {
        return this.formatDate(isoDate);
      }
    }

    return stringValue;
  };

  formatDate = (date: Date): string => {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
      hour12: true,
      timeZoneName: 'short',
      ...(this.args.timezone ? { timeZone: this.args.timezone } : {}),
    });
  };

  formatTimeWithMilliseconds = (date: Date): string => {
    return this.#formatTime(date, true);
  };

  formatKey = (key: string): string => {
    // handle special field name mappings
    if (key === 'default_location_id') {
      return 'Primary location';
    }

    // handle nested keys with dots (e.g. 'field.nested' -> 'Field > Nested')
    if (key.includes('.')) {
      return key
        .split('.')
        .map((part) => {
          const formatted = part.replace(UNDERSCORE_PATTERN, ' ').replace(CAMEL_CASE_PATTERN, '$1 $2').toLowerCase();
          return capitalize(formatted);
        })
        .join(' > ');
    }

    const formatted = key.replace(UNDERSCORE_PATTERN, ' ').replace(CAMEL_CASE_PATTERN, '$1 $2').toLowerCase();
    return capitalize(formatted);
  };

  displayChangeValue = (value: string | number | boolean | null | Record<string, unknown>, key?: string): string => {
    if (value === null || value === undefined) {
      return '(blank)';
    }

    // handle object values by converting to formatted JSON
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value, null, 2);
    }

    // special handling for location IDs to show location names
    if (key === 'default_location_id') {
      return this.#getLocationName(String(value));
    }

    return this.formatValue(value);
  };

  getIndentationStyle = (level?: number): SafeString => {
    if (!level || level === 0) return htmlSafe('');
    return htmlSafe(`margin-left: ${level * 16}px;`);
  };

  #trackDrawerOpen(): void {
    this.metrics.trackEvent('Event Log Drawer Opened', {
      event_type: this.args.event.eventType,
      company_id: this.args.event.companyId,
      location_id: this.args.event.locationId,
      actor_type: this.args.event.actorType,
      target_type: this.args.event.targetType,
    });
  }

  #getLocationName = (locationId: string): string => {
    if (locationId) {
      const location = this.skinnyLocations.content?.find((loc) => {
        return loc.id === locationId;
      });
      return location ? location.name : 'Unknown location';
    }
    return '-';
  };

  #flattenEventData(
    obj: Record<string, unknown>,
    level: number,
    parentId: string,
  ): Array<{ key: string; value: unknown; isNested?: boolean; level?: number; id?: string; parentId?: string }> {
    const entries: Array<{
      key: string;
      value: unknown;
      isNested?: boolean;
      level?: number;
      id?: string;
      parentId?: string;
    }> = [];
    const excludedFields = this.#getExcludedFields(this.args.event.eventType);
    const conditionalFields = this.#getConditionalDisplayFields(this.args.event.eventType);

    Object.entries(obj).forEach(([originalKey, value]) => {
      // skip fields that are in the exclusion list
      if (excludedFields.includes(originalKey)) {
        return;
      }

      // skip conditional fields that should be hidden
      if (conditionalFields.includes(originalKey) && this.#shouldHideConditionalField(value)) {
        return;
      }

      const formattedKey = this.formatKey(originalKey);
      const fieldId = parentId ? `${parentId}.${originalKey}` : originalKey;

      if (typeof value === 'object' && value !== null) {
        if (Array.isArray(value)) {
          // handle arrays
          if (value.length === 0) {
            entries.push({ key: formattedKey, value: null, level, id: fieldId });
          } else {
            // check if array contains objects
            const hasObjects = value.some((item) => typeof item === 'object' && item !== null && !Array.isArray(item));

            if (hasObjects) {
              // array with objects - add array header and flatten each object
              entries.push({ key: formattedKey, value: '', isNested: true, level, id: fieldId });

              if (!this.isFieldCollapsed(fieldId)) {
                value.forEach((item, index) => {
                  const itemId = `${fieldId}[${index}]`;
                  if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
                    // object in array - add index header and flatten the object
                    entries.push({
                      key: `Item ${index + 1}`,
                      value: '',
                      isNested: true,
                      level: level + 1,
                      id: itemId,
                      parentId: fieldId,
                    });
                    if (!this.isFieldCollapsed(itemId)) {
                      const nestedEntries = this.#flattenEventData(item as Record<string, unknown>, level + 2, itemId);
                      entries.push(...nestedEntries);
                    }
                  } else {
                    // non-object in array - add as regular entry
                    entries.push({
                      key: `Item ${index + 1}`,
                      value: item,
                      level: level + 1,
                      id: itemId,
                      parentId: fieldId,
                    });
                  }
                });
              }
            } else {
              // array with primitives only
              entries.push({ key: formattedKey, value: value, level, id: fieldId });
            }
          }
        } else {
          // handle objects
          const objectKeys = Object.keys(value as Record<string, unknown>);
          if (objectKeys.length === 0) {
            // empty object - treat as a regular value
            entries.push({ key: formattedKey, value: null, level, id: fieldId });
          } else {
            // add the parent object key
            entries.push({ key: formattedKey, value: '', isNested: true, level, id: fieldId });

            // add nested entries with increased level only if not collapsed
            if (!this.isFieldCollapsed(fieldId)) {
              const nestedEntries = this.#flattenEventData(value as Record<string, unknown>, level + 1, fieldId);
              entries.push(...nestedEntries);
            }
          }
        }
      } else {
        // handle location ID resolution for event data
        const processedValue = originalKey === 'default_location_id' ? this.#getLocationName(String(value)) : value;
        entries.push({ key: formattedKey, value: processedValue, level, id: fieldId, parentId });
      }
    });

    return entries;
  }

  #formatTime = (date: Date, includeMilliseconds: boolean = false): string => {
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
      ...(this.args.timezone ? { timeZone: this.args.timezone } : {}),
    };

    if (includeMilliseconds) {
      options.fractionalSecondDigits = 3;
    }

    return date.toLocaleString('en-US', options);
  };

  /**
   * Gets all fields that should be excluded from display for a given event type.
   * This is needed to hide sensitive or unnecessary data like internal IDs
   * from the event log drawer. Combines default exclusions with specific event-type ones.
   */
  #getExcludedFields(eventType: string): string[] {
    const excludedFields = new Set<string>();

    // add default exclusions
    const defaultExclusions = FIELD_EXCLUSIONS['*'] || [];
    defaultExclusions.forEach((field) => excludedFields.add(field));

    // check for pattern matches (e.g., 'employee.*')
    Object.keys(FIELD_EXCLUSIONS).forEach((pattern) => {
      if (pattern !== '*' && this.#matchesPattern(eventType, pattern)) {
        const fields = FIELD_EXCLUSIONS[pattern] || [];
        fields.forEach((field) => excludedFields.add(field));
      }
    });

    // check for exact match
    const exactMatch = FIELD_EXCLUSIONS[eventType];
    if (exactMatch) {
      exactMatch.forEach((field) => excludedFields.add(field));
    }

    return Array.from(excludedFields);
  }

  /**
   * Gets fields that should only be displayed when they have non-empty values.
   * This allows certain fields to be conditionally shown based on their content.
   */
  #getConditionalDisplayFields(eventType: string): string[] {
    const conditionalFields = new Set<string>();

    // check for pattern matches (e.g., 'employee.*')
    Object.keys(CONDITIONAL_DISPLAY_FIELDS).forEach((pattern) => {
      if (this.#matchesPattern(eventType, pattern)) {
        const fields = CONDITIONAL_DISPLAY_FIELDS[pattern] || [];
        fields.forEach((field) => conditionalFields.add(field));
      }
    });

    // check for exact match
    const exactMatch = CONDITIONAL_DISPLAY_FIELDS[eventType];
    if (exactMatch) {
      exactMatch.forEach((field) => conditionalFields.add(field));
    }

    return Array.from(conditionalFields);
  }

  /**
   * Determines if a conditional field should be hidden based on its value.
   * Note: for boolean fields, we show both true and false values.
   */
  #shouldHideConditionalField(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return false;
    }
    return isEmpty(value);
  }

  /**
   * Checks if an event type matches a pattern from the exclusion mapping.
   * This enables wildcard matching like 'employee.*' to match 'employee.created'.
   * Used to apply exclusion rules to entire event type categories without specifying each event individually.
   */
  #matchesPattern(eventType: string, pattern: string): boolean {
    if (pattern.endsWith('*')) {
      const prefix = pattern.slice(0, -1);
      return eventType.startsWith(prefix);
    }
    return eventType === pattern;
  }

  /**
   * Recursively flattens nested changes objects until we find from/to pairs.
   * Converts { field: { nested: { from: 'a', to: 'b' } } }
   * to { 'field.nested': { from: 'a', to: 'b' } }
   *
   * If an object doesn't contain any from/to pairs at any level, it will be ignored.
   */
  #flattenChanges(obj: Record<string, unknown>, prefix = ''): ChangesRecord {
    const flattened: ChangesRecord = {};

    Object.entries(obj).forEach(([key, value]) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;

      if (isFromToObject(value)) {
        // found a from/to pair, add it to the flattened result
        flattened[fullKey] = value;
      } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        // still a nested object, continue flattening recursively
        const nestedFlattened = this.#flattenChanges(value as Record<string, unknown>, fullKey);
        Object.assign(flattened, nestedFlattened);
      }
      // if it's not a from/to object and not a nested object, ignore it
      // this prevents primitive values or arrays from being treated as changes
    });

    return flattened;
  }

  <template>
    {{#if @isOpen}}
      <TableDrawer data-test-event-log-drawer>
        {{#if (has-block)}}
          {{! Teams can provide custom drawer content }}
          {{yield @event}}
        {{else}}
          {{! Default drawer content }}
          <div class="w-full p-8">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm text-carbon-40 font-medium mb-6" data-test-event-datetime>
                  {{#if @event.dateTime}}
                    {{dateFormat @event.dateTime "MMM d, yyyy" timezone=@timezone}}
                    &bull;
                    {{this.formatTimeWithMilliseconds @event.dateTime}}
                  {{else}}
                    -
                  {{/if}}
                </p>
                <h3 class="mb-1">{{if @event.eventTypeName @event.eventTypeName "-"}}</h3>
                <p class="text-sm text-carbon-70 mb-0" data-test-event-description>
                  {{if @event.eventDescription @event.eventDescription "-"}}
                </p>
              </div>
              <UiButton
                class="btn btn--small btn--outlineFill !bg-white"
                data-test-close-button
                {{on "click" this.close}}
              >
                {{svgJar "cross-black" height="16" width="16" class="-mb-1"}}
              </UiButton>
            </div>
          </div>

          <div class="flex w-full px-8 gap-4">
            <div class={{if @event.targetName "w-1/2" "w-full"}}>
              <p class="text-sm text-carbon-40 font-medium !mb-0">Performed by</p>
              <UiCard class="border-carbon-10 min-h-[76px]">
                <div class="flex items-center gap-2">
                  {{#if (eq @event.actorType "system")}}
                    <div class="mx-1 w-10 h-10 bg-carbon-10 rounded-full flex items-center justify-center">
                      <span class="flex" style="padding-left: 0.05rem;">
                        {{svgJar "logo-small-red" width="18" height="18"}}
                      </span>
                    </div>
                  {{else}}
                    <MonogramOrPhoto
                      class="!p-0"
                      @name={{if @event.actorName @event.actorName "-"}}
                      @medium={{true}}
                      @borderBottom={{false}}
                    />
                  {{/if}}
                  <div>
                    <p class="text-sm text-carbon-70 !mb-0" data-test-event-actor>
                      {{if @event.actorName @event.actorName "-"}}
                    </p>
                    {{#if this.lowercaseActorType}}
                      <p class="text-sm text-carbon-40 !mb-0" data-test-event-actor-type>
                        {{this.lowercaseActorType}}
                      </p>
                    {{/if}}
                  </div>
                </div>
              </UiCard>
            </div>

            {{#if @event.targetName}}
              <div class="w-1/2">
                <p class="text-sm text-carbon-40 font-medium !mb-0">Impacted item</p>
                <UiCard class="border-carbon-10 min-h-[76px]">
                  <div class="flex items-center gap-2">
                    <MonogramOrPhoto
                      class="!p-0"
                      @name={{@event.targetName}}
                      @medium={{true}}
                      @borderBottom={{false}}
                    />
                    <div>
                      <p class="text-sm text-carbon-70 !mb-0" data-test-event-target>
                        {{@event.targetName}}
                      </p>
                      {{#if this.lowercaseTargetType}}
                        <p class="text-sm text-carbon-40 !mb-0" data-test-event-target-type>
                          {{this.lowercaseTargetType}}
                        </p>
                      {{/if}}
                    </div>
                  </div>
                </UiCard>
              </div>
            {{/if}}
          </div>

          <div class="w-full p-8">
            {{#if this.hasChanges}}
              <p class="text-sm text-carbon-40 font-medium !mb-0">Changes</p>
              <UiCard class="border-carbon-10 mb-8" data-test-event-changes>
                <div class="divide-y divide-carbon-10">
                  {{#each-in this.changes as |key value|}}
                    <div class="py-3 first:pt-0 last:pb-0">
                      <div class="text-sm text-carbon-40 mb-1">{{this.formatKey key}}</div>
                      <div class="text-sm text-carbon-70 break-words">
                        <span class="text-carbon-40">
                          {{this.displayChangeValue value.from key}}
                        </span>
                        <span class="text-gem-50 mx-1">&rarr;</span>
                        <span>
                          {{this.displayChangeValue value.to key}}
                        </span>
                      </div>
                    </div>
                  {{/each-in}}
                </div>
              </UiCard>
            {{/if}}

            <p class="text-sm text-carbon-40 font-medium !mb-0">Details</p>
            <UiCard class="border-carbon-10 mb-4">
              {{#if this.eventCategoryComponent}}
                {{! Render category-specific component }}
                <this.eventCategoryComponent
                  @eventType={{@event.eventType}}
                  @eventData={{@event.event}}
                  @formatValue={{this.formatValue}}
                />
              {{else}}
                <div data-test-default-event-data data-test-event-data>
                  {{! Default rendering for events without category-specific components }}
                  {{#if this.visitorTargetLink}}
                    <div class="pb-3 border-b border-carbon-10" data-test-event-visitor-link>
                      <div class="text-sm text-carbon-40 mb-1">{{this.visitorTargetLink.label}}</div>
                      <div class="text-sm">
                        <LinkTo
                          @route={{this.visitorTargetLink.route}}
                          @model={{this.visitorTargetLink.model}}
                          class="text-carbon-70 hover:text-red-50 underline underline-offset-2"
                          data-test-event-visitor-link-anchor
                        >
                          {{@event.targetName}}
                        </LinkTo>
                      </div>
                    </div>
                  {{/if}}

                  {{#if @event.locationName}}
                    <div
                      class="{{if this.visitorTargetLink 'py-3' 'pb-3'}} border-b border-carbon-10"
                      data-test-event-location
                    >
                      <div class="text-sm text-carbon-40 mb-1">Location</div>
                      <div class="text-sm text-carbon-70">
                        {{@event.locationName}}
                      </div>
                    </div>
                  {{/if}}

                  {{#if @event.ipAddress}}
                    <div
                      class="{{if (or @event.locationName this.visitorTargetLink) 'py-3' 'pb-3'}}
                        border-b border-carbon-10"
                      data-test-event-ip-address
                    >
                      <div class="text-sm text-carbon-40 mb-1">IP address</div>
                      <div class="text-sm text-carbon-70">
                        {{@event.ipAddress}}{{#if @event.country}}
                          <span class="text-carbon-40"> ({{@event.country}})</span>
                        {{/if}}
                      </div>
                    </div>
                  {{/if}}

                  {{#if this.eventDataEntries.length}}
                    {{#each this.eventDataEntries as |entry index|}}
                      {{#if entry.isNested}}
                        <div
                          class="{{if (and (gt index 0) (eq entry.level 0)) 'border-t border-carbon-10'}}
                            py-3 first:pt-0 last:pb-0 cursor-pointer hover:bg-carbon-5"
                          style={{this.getIndentationStyle entry.level}}
                          {{on "click" (fn this.toggleFieldCollapse entry.id)}}
                        >
                          <div class="text-sm text-carbon-40 font-medium flex items-center gap-2">
                            {{entry.key}}
                            {{#if (this.isFieldCollapsed entry.id)}}
                              {{svgJar "chevron-down-gray" class="" width="8" height="8"}}
                            {{else}}
                              {{svgJar "chevron-up-gray" class="" width="8" height="8"}}
                            {{/if}}
                          </div>
                        </div>
                      {{else}}
                        <div
                          class="{{if (and (gt index 0) (eq entry.level 0)) 'border-t border-carbon-10'}}
                            py-3 first:pt-0 last:pb-0"
                          style={{this.getIndentationStyle entry.level}}
                        >
                          <div class="text-sm text-carbon-40 mb-1">{{entry.key}}</div>
                          <div class="text-sm text-carbon-70">
                            {{this.formatValue entry.value}}
                          </div>
                        </div>
                      {{/if}}
                    {{/each}}
                  {{else}}
                    <div class="text-sm text-carbon-40 text-center py-4" data-test-no-event-data>
                      No additional event data available
                    </div>
                  {{/if}}
                </div>
              {{/if}}
            </UiCard>
          </div>
        {{/if}}
      </TableDrawer>
    {{/if}}
  </template>
}
